<div class="container">

  <header class="page-heading">
    <h1>TDS on Property Sale Calculator (Form 141/26QB)</h1>
    <p>Calculate the TDS deductible on a property sale under Section 393(1) of the Income-tax Act, 2025 (resident sellers, filed via Form 141/Schedule B — the old Section 194-IA/Form 26QB before 1 April 2026) or Section 393(2) (NRI sellers, old Section 195), plus the state stamp duty on the transaction — and any interest or late fee for a delayed deposit.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">Property Sale Details</div>

    <div class="field-group">
      <!-- Group heading for the residency toggle buttons below, wired via aria-labelledby (not a single-control label) -->
      <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
      <label class="field-label" id="residency-label">Seller's Residential Status</label>
      <div class="mode-toggle" role="group" aria-labelledby="residency-label">
        <button type="button" class="mode-btn" [class.active]="residency==='resident'" (click)="setResidency('resident')">
          Resident Indian
        </button>
        <button type="button" class="mode-btn" [class.active]="residency==='nri'" (click)="setResidency('nri')">
          NRI / Non-Resident
        </button>
      </div>
    </div>

    <div class="field-group">
      <label class="field-label" for="state">State / Union Territory</label>
      <div class="input-shell">
        <select id="state" [(ngModel)]="state" (ngModelChange)="onFieldChange('state')">
          @for (s of states; track s.name) {
            <option [value]="s.name">{{ s.name }}</option>
          }
        </select>
      </div>
      <div class="field-hint">
        Used only to estimate the state's stamp duty/registration cost below — the TDS rate itself
        is a flat national rate under the Income Tax Act, not state-dependent.
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('saleConsideration')">
        <label class="field-label" for="saleConsideration">Sale Consideration (Agreement Value)</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="saleConsideration" type="number"
            [(ngModel)]="saleConsideration"
            [min]="C.saleConsideration.min" [max]="C.saleConsideration.max" [step]="C.saleConsideration.step"
            (ngModelChange)="onFieldChange('saleConsideration')"
            (blur)="onFieldBlur('saleConsideration')"
            placeholder="e.g. 60,00,000"
            autocomplete="off"/>
        </div>
        @if (hasError('saleConsideration')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('saleConsideration') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('stampDutyValue')">
        <label class="field-label" for="stampDutyValue">Stamp Duty Value (Circle Rate/Guideline Value)</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="stampDutyValue" type="number"
            [(ngModel)]="stampDutyValue"
            [min]="C.stampDutyValue.min" [max]="C.stampDutyValue.max" [step]="C.stampDutyValue.step"
            (ngModelChange)="onFieldChange('stampDutyValue')"
            (blur)="onFieldBlur('stampDutyValue')"
            placeholder="Leave 0 to use sale consideration"
            autocomplete="off"/>
        </div>
        @if (hasError('stampDutyValue')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('stampDutyValue') }}
          </div>
        } @else {
          <div class="field-hint">
            As assessed by the sub-registrar for stamp duty. If this is higher than the sale
            consideration, TDS is computed on this value instead.
          </div>
        }
      </div>
    </div>

    @if (residency === 'resident') {
      <div class="field-group">
        <!-- Group heading for the PAN-available toggle buttons below, wired via aria-labelledby (not a single-control label) -->
        <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
        <label class="field-label" id="pan-available-label">Seller's PAN Available to Buyer?</label>
        <div class="mode-toggle" role="group" aria-labelledby="pan-available-label">
          <button type="button" class="mode-btn" [class.active]="panAvailable===true" (click)="setPanAvailable(true)">
            Yes
          </button>
          <button type="button" class="mode-btn" [class.active]="panAvailable===false" (click)="setPanAvailable(false)">
            No
          </button>
        </div>
        <div class="field-hint">
          Without a valid PAN, TDS under Section 206AA is deducted at 20% instead of 1%.
        </div>
      </div>

      <div class="field-row-2">
        <div class="field-group">
          <label class="field-label" for="dateOfDeduction">Date of TDS Deduction</label>
          <div class="input-shell">
            <input id="dateOfDeduction" type="date"
              [(ngModel)]="dateOfDeduction"
              (ngModelChange)="onFieldChange('dateOfDeduction')"
              autocomplete="off"/>
          </div>
        </div>
        <div class="field-group">
          <label class="field-label" for="dateOfDeposit">Date of Filing / TDS Deposit</label>
          <div class="input-shell">
            <input id="dateOfDeposit" type="date"
              [(ngModel)]="dateOfDeposit"
              (ngModelChange)="onFieldChange('dateOfDeposit')"
              autocomplete="off"/>
          </div>
          <div class="field-hint">
            The TDS statement (Form 141/Schedule B on or after 1 April 2026, Form 26QB before that)
            must be filed, and TDS deposited, within 30 days from the end of the month of deduction.
            A delay here attracts interest and a late filing fee.
          </div>
        </div>
      </div>
    } @else {
      <div class="field-group">
        <!-- Group heading for the holding-period toggle buttons below, wired via aria-labelledby (not a single-control label) -->
        <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
        <label class="field-label" id="holding-period-label">Property Holding Period</label>
        <div class="mode-toggle" role="group" aria-labelledby="holding-period-label">
          <button type="button" class="mode-btn" [class.active]="holdingPeriod==='longTerm'" (click)="setHoldingPeriod('longTerm')">
            More than 24 months (LTCG)
          </button>
          <button type="button" class="mode-btn" [class.active]="holdingPeriod==='shortTerm'" (click)="setHoldingPeriod('shortTerm')">
            24 months or less (STCG)
          </button>
        </div>
        <div class="field-hint">
          For NRI sellers, TDS under Section 195 applies from the first rupee (no ₹50 lakh
          threshold) and, absent a lower/nil-deduction certificate, is typically withheld on the
          full consideration.
        </div>
      </div>
    }

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">TDS to be Deducted</div>
        <div class="sc-value">{{ fmt.currency(result.tdsAmount) }}</div>
        <div class="sc-sub">{{ (result.baseRate * 100).toFixed(2) }}% base rate on {{ fmt.currency(result.considerationForTds) }}</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Net Amount to Seller</div>
        <div class="sc-value">{{ fmt.currency(result.netAmountToSeller) }}</div>
        <div class="sc-sub">after TDS is withheld by the buyer</div>
      </div>
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Est. State Stamp Duty</div>
        <div class="sc-value">{{ fmt.currency(result.estimatedStampDutyCost) }}</div>
        <div class="sc-sub">{{ state }} @ {{ (result.stateStampDutyRate * 100).toFixed(2) }}% (indicative)</div>
      </div>
    </div>

    @if (residency === 'resident' && !result.tdsApplicable) {
      <div class="card mb-md">
        <div class="section-title">No TDS Applicable</div>
        <p>
          Sale/stamp duty consideration of {{ fmt.currency(result.considerationForTds) }} is below the
          ₹50,00,000 threshold under Section 393(1), so the buyer is not required to deduct TDS on
          this transaction.
        </p>
      </div>
    }

    @if (result.filingCompliance) {
      <div class="card mb-md">
        <div class="section-title">{{ result.filingCompliance.formLabel }} Deposit &amp; Filing</div>
        <div class="tbl-wrap">
          <table>
            <tbody>
              <tr>
                <td class="t-left">Due Date to File {{ result.filingCompliance.formLabel }} &amp; Deposit TDS</td>
                <td class="t-balance">{{ result.filingCompliance.dueDate | date:'d MMM y' }}</td>
              </tr>
              @if (result.filingCompliance.daysLate === 0) {
                <tr>
                  <td class="t-left" colspan="2">
                    Filed on or before the due date — no interest or late fee applies.
                  </td>
                </tr>
              } @else {
                <tr>
                  <td class="t-left">Days Late</td>
                  <td class="t-balance">{{ result.filingCompliance.daysLate }}</td>
                </tr>
                <tr>
                  <td class="t-left">Interest for Late Deposit (1.5%/month, Sec 398(3)(a))</td>
                  <td class="t-balance">{{ fmt.currency(result.filingCompliance.lateDepositInterest) }}</td>
                </tr>
                <tr>
                  <td class="t-left">Late Filing Fee (₹200/day, Sec 427)</td>
                  <td class="t-balance">{{ fmt.currency(result.filingCompliance.lateFilingFee) }}</td>
                </tr>
                <tr class="prepay-highlight">
                  <td class="t-left"><strong>= Total Payable</strong></td>
                  <td class="t-balance"><strong>{{ fmt.currency(result.filingCompliance.totalPayable) }}</strong></td>
                </tr>
                <tr>
                  <td class="t-left" colspan="2">
                    Prolonged or repeated non-filing can additionally draw a discretionary
                    penalty of ₹10,000–₹1,00,000 under Section 461, on top of the amounts above.
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }

    <div class="card charts-card mb-md">
      <div class="section-title">Sale Consideration Breakdown</div>
      <div class="chart-panel">
        <div class="chart-canvas-wrap chart-canvas-bar-solo">
          <canvas #breakdownRef></canvas>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">TDS Calculation Breakdown</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Component</th>
              <th class="t-balance">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="t-left">Sale Consideration</td>
              <td class="t-balance">{{ fmt.currency(saleConsideration) }}</td>
            </tr>
            <tr>
              <td class="t-left">Stamp Duty Value</td>
              <td class="t-balance">{{ fmt.currency(stampDutyValue || saleConsideration) }}</td>
            </tr>
            <tr class="prepay-highlight">
              <td class="t-left"><strong>= Consideration Used for TDS ({{ result.usedStampDutyValue ? 'stamp duty value is higher' : 'sale consideration' }})</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.considerationForTds) }}</strong></td>
            </tr>
            <tr>
              <td class="t-left">Base TDS Rate</td>
              <td class="t-balance">{{ (result.baseRate * 100).toFixed(2) }}%</td>
            </tr>
            @if (residency === 'nri') {
              <tr>
                <td class="t-left">Surcharge ({{ (result.surchargeRate * 100).toFixed(0) }}%)</td>
                <td class="t-balance">{{ fmt.currency(result.surchargeAmount) }}</td>
              </tr>
              <tr>
                <td class="t-left">Health &amp; Education Cess (4%)</td>
                <td class="t-balance">{{ fmt.currency(result.cessAmount) }}</td>
              </tr>
            }
            <tr>
              <td class="t-left"><strong>= Total TDS</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.tdsAmount) }}</strong></td>
            </tr>
            <tr>
              <td class="t-left"><strong>= Net Amount to Seller</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.netAmountToSeller) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">What is Form 141 (formerly Form 26QB) and How is TDS on Property Sale Calculated?</h2>
    <p>
      When a property in India is sold for ₹50 lakh or more, the <strong>buyer</strong> (not the
      seller) is required to deduct TDS under <strong>Section 393(1) of the Income-tax Act,
      2025</strong> and deposit it with the government using <strong>Form 141 (Schedule B)</strong>
      — a combined challan-cum-statement filed online on the Income Tax e-filing portal — before
      paying the balance to the seller. Form 141 replaced the old Form 26QB (filed under the old
      Section 194-IA) from 1 April 2026; deals whose TDS was deducted before that date remain
      governed by the old form and section. This applies uniformly across every state — the TDS
      rate itself does not vary by where the property is located.
    </p>
    <ul class="emi-formula-vars">
      <li>Rate: 1% of the sale consideration (or the stamp duty value, if higher) — unchanged by
        the Income-tax Act, 2025</li>
      <li>Without a valid PAN, the rate rises to 20% under Section 397(2) (old Section 206AA)</li>
      <li>No TDS is required if the consideration is below ₹50 lakh, and agricultural land is
        excluded regardless of value</li>
      <li>Form 141/Schedule B (Form 26QB before 1 April 2026), along with the TDS payment, must be
        filed within 30 days from the end of the month in which TDS was deducted</li>
      <li>Missing that deadline attracts interest at 1.5% per month (or part) under Section
        398(3)(a) (old Section 201(1A)), plus a late filing fee of ₹200/day under Section 427 (old
        Section 234E), capped at the TDS amount</li>
      <li>Prolonged or repeated non-filing can additionally draw a discretionary penalty of
        ₹10,000–₹1,00,000 under Section 461 (old Section 271H)</li>
      <li>After filing, the buyer must issue <strong>Form 132</strong> (still widely called
        "Form 16B") to the seller as proof of TDS, downloadable from the TRACES portal</li>
      <li>If the seller is an NRI, Section 393(2) applies instead (old Section 195; not Form 141,
        but Form 27Q) — TDS is withheld from the first rupee, at rates for long-term (12.5%) or
        short-term (slab-rate, taken as 30% by default) capital gains, plus surcharge and 4% cess</li>
    </ul>
    <p>
      What genuinely varies by state is the <strong>stamp duty</strong> paid to register the sale
      deed — shown above as an indicative estimate for the selected state.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Do I still file Form 26QB, or has it been replaced?</h3>
      <p>
        It has been replaced. From 1 April 2026, Form 26QB no longer applies to new property
        transactions — TDS on the sale of immovable property is now reported through Form 141
        (Schedule B) under Section 393(1) of the Income-tax Act, 2025. Form 26QB remains relevant
        only for consideration paid or credited before that date, which falls under the old
        Section 194-IA of the Income-tax Act, 1961.
      </p>
    </div>
    <div class="faq-item">
      <h3>Does the TDS rate on property sale differ from state to state?</h3>
      <p>
        No. TDS under Section 393(1) (resident sellers) and Section 393(2) (NRI sellers) is
        governed by the central Income Tax Act and applies at the same rate nationwide. Only the
        stamp duty charged to register the sale deed is state-specific.
      </p>
    </div>
    <div class="faq-item">
      <h3>Who deducts the TDS — the buyer or the seller?</h3>
      <p>
        The buyer deducts TDS from the amount payable to the seller and deposits it with the
        government (Form 141/Schedule B for resident sellers), then issues Form 132 (still widely
        called Form 16B) to the seller as proof.
      </p>
    </div>
    <div class="faq-item">
      <h3>Does this TDS apply to agricultural land?</h3>
      <p>
        No. Agricultural land, as defined under the Income Tax Act, is specifically excluded from
        this TDS — no obligation arises regardless of the sale value, the same exclusion that
        existed under the old Section 194-IA.
      </p>
    </div>
    <div class="faq-item">
      <h3>Can an NRI seller reduce this TDS?</h3>
      <p>
        Yes. An NRI seller can apply to the Assessing Officer in Form 13 for a lower or nil TDS
        deduction certificate, based on their actual estimated capital gains rather than the full
        sale consideration. This calculator estimates TDS on the full consideration, as is
        typically withheld absent such a certificate.
      </p>
    </div>
    <div class="faq-item">
      <h3>Is the ₹50 lakh threshold per buyer or per seller?</h3>
      <p>
        It applies to the total sale consideration of the property. Splitting a single property
        transaction across multiple joint buyers or sellers to stay under ₹50 lakh each does not
        exempt the transaction from TDS. Under the Income-tax Act, 2025, a single Form 141 can
        now cover multiple buyers or multiple sellers in one filing, as long as everyone on that
        side of the deal shares the same category (all individuals, or all companies) — but the
        ₹50 lakh threshold still applies to the total consideration, not each party's share.
      </p>
    </div>
    <div class="faq-item">
      <h3>What is the due date for filing and depositing the TDS?</h3>
      <p>
        Form 141/Schedule B (or Form 26QB, for deductions before 1 April 2026) must be filed, and
        the TDS deposited, within 30 days from the end of the month in which the deduction was
        made. For example, if TDS is deducted on 10 June, the due date is 30 days after 30 June,
        i.e. 30 July.
      </p>
    </div>
    <div class="faq-item">
      <h3>What happens if the filing is late?</h3>
      <p>
        Three consequences can apply: interest at 1.5% per month (or part of a month) on the TDS
        amount from the date of deduction to the date of deposit under Section 398(3)(a) (old
        Section 201(1A)); a late filing fee of ₹200 for every day of delay under Section 427 (old
        Section 234E), capped at the TDS amount itself; and, for prolonged or repeated non-filing,
        a discretionary penalty of ₹10,000–₹1,00,000 that an assessing officer may additionally
        levy under Section 461 (old Section 271H).
      </p>
    </div>
    <div class="faq-item">
      <h3>What is Form 132 (Form 16B)?</h3>
      <p>
        Form 132 — still widely called "Form 16B" out of habit — is the TDS certificate the buyer
        must download from the TRACES portal and issue to the seller after the Form 141/Schedule B
        (or, before 1 April 2026, Form 26QB) filing has been processed. It is the seller's proof
        that TDS was deducted and deposited against the sale, needed to claim credit in their own
        return.
      </p>
    </div>
    <div class="faq-item">
      <h3>Can the TDS be deposited before the property registration date?</h3>
      <p>
        Yes. TDS liability under Section 393(1) arises when the consideration is paid or credited
        to the seller — whichever is earlier — not on the registration date itself. If any payment
        is made before registration, the filing should be made and the TDS deposited within 30 days
        from the end of that month, regardless of when the sale deed is registered. In fact, many
        sub-registrar offices ask to see the TDS challan before they will register a high-value
        sale, so depositing TDS ahead of registration is common practice, not just permitted.
      </p>
    </div>
    <p class="learn-crosslink">
      This calculator provides an estimate for planning purposes only — stamp duty rates and TDS
      provisions change periodically; confirm exact figures with your bank, buyer/seller and a
      qualified CA before the transaction.
    </p>
  </div>
</section>
